import { CircleCheck, CircleX, FlaskConical } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toValueLabel } from "~/utils/form-utils";
import { SAMPLE_FILENAMES, SAMPLE_PATHS, testPattern, validatePatternWithSamples } from "~/utils/pattern-utils";
import { Collapse, Compact, Input, Select, Tag, Text } from "~/components/ui";

interface PatternEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  mode?: "path" | "filename";
  /** Schema regex_flags (IGNORECASE/MULTILINE/DOTALL) so the preview matches server behavior. */
  regexFlags?: string[] | null;
}

// Patterns are raw regexes evaluated server-side with Python's re.search.
// Slashes are literal characters (path separators in the input), NOT delimiters.
// Labels resolve at render via t(`schemaAdmin.pattern.presetLabels.${labelKey}`);
// regex values and example descriptions are data, not UI text.
const PATH_PRESETS = [
  { labelKey: "sfpSector", value: "/(0000|1000|2000|3000)-", description: "0000, 1000, 2000, 3000" },
  { labelKey: "year", value: "/(20\\d{2})/", description: "2020, 2021, 2022..." },
  { labelKey: "revisionNumber", value: "[Rr]ev\\.?\\s*(\\d+)", description: "Rev.1, rev 2, R3" },
  { labelKey: "alphanumericCode", value: "([A-Z]{2,4}-\\d{3,6})", description: "ABC-123, PROC-12345" },
];

const FILENAME_PRESETS = [
  { labelKey: "leadingCode", value: "^([A-Z]{2,4}-\\d{3,6})", description: "PROC-001, UT-12345" },
  { labelKey: "trailingRevision", value: "-R(\\d+)\\.[^.]+$", description: "-R01.pdf, -R2.docx" },
  { labelKey: "withoutExtension", value: "^(.+)\\.[^.]+$", description: "filename" },
  {
    labelKey: "planNumber",
    value: "(\\d{4}-[A-Z]{2}-\\d{3}[A-Z]?-\\d{3}-\\d{3})",
    description: "2000-CV-030A-100-001",
  },
];

export function PatternEditor({ value, onChange, placeholder, mode = "path", regexFlags }: PatternEditorProps) {
  const { t } = useTranslation("admin");
  const [showTester, setShowTester] = useState(false);

  const presetsRaw = mode === "filename" ? FILENAME_PRESETS : PATH_PRESETS;
  const presets = presetsRaw.map((preset) => ({
    ...preset,
    label: t(`schemaAdmin.pattern.presetLabels.${preset.labelKey}`),
  }));
  const samples = mode === "filename" ? SAMPLE_FILENAMES : SAMPLE_PATHS;
  const [testInput, setTestInput] = useState(samples[0]);

  const { isValid, matches, error } = useMemo(
    () => validatePatternWithSamples(value, samples, regexFlags),
    [value, samples, regexFlags]
  );

  const testResult = useMemo(
    () => (value && testInput ? testPattern(value, testInput, regexFlags) : null),
    [value, testInput, regexFlags]
  );

  return (
    <div className="space-y-2">
      <Compact className="w-full">
        <Input
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder ?? "([A-Z]+-\\d+)"}
          aria-label={t("schemaAdmin.pattern.expression")}
          className="font-mono"
          status={value && !isValid ? "error" : undefined}
        />
        <Select
          placeholder={t("schemaAdmin.pattern.presets")}
          aria-label={t("schemaAdmin.pattern.presets")}
          className="w-40 shrink-0"
          options={presets}
          onChange={(preset) => onChange?.(preset)}
          value={null}
          allowClear
        />
      </Compact>

      {value && (
        <div className="flex items-center gap-2 flex-wrap">
          {isValid ? (
            <>
              <Tag color="success" icon={<CircleCheck size={14} />}>
                {t("schemaAdmin.pattern.valid")}
              </Tag>
              {matches.length > 0 && (
                <Text tone="muted" className="text-xs">
                  {t("schemaAdmin.pattern.sampleMatches")}:{" "}
                  {matches.map((m, i) => (
                    <code key={i} className="bg-noa-inset text-green-600 px-1 mx-0.5 rounded">
                      {m}
                    </code>
                  ))}
                </Text>
              )}
            </>
          ) : (
            <Tag color="error" icon={<CircleX size={14} />}>
              {error}
            </Tag>
          )}
        </div>
      )}

      <Collapse
        size="small"
        ghost
        activeKey={showTester ? ["tester"] : []}
        onChange={(keys) => setShowTester(keys.includes("tester"))}
        items={[
          {
            key: "tester",
            label: (
              <Text tone="muted" className="text-xs">
                <FlaskConical size={14} className="mr-1" />
                {t("schemaAdmin.pattern.testCustom")}
              </Text>
            ),
            children: (
              <div className="space-y-2">
                <Input
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  placeholder="/path/to/file.pdf"
                  aria-label={t("schemaAdmin.pattern.testCustom")}
                  className="font-mono text-sm"
                />
                <div className="flex items-center gap-2">
                  <Select
                    size="small"
                    placeholder={
                      mode === "filename"
                        ? t("schemaAdmin.pattern.sampleFilenames")
                        : t("schemaAdmin.pattern.samplePaths")
                    }
                    aria-label={
                      mode === "filename"
                        ? t("schemaAdmin.pattern.sampleFilenames")
                        : t("schemaAdmin.pattern.samplePaths")
                    }
                    options={samples.map(toValueLabel)}
                    onChange={setTestInput}
                    className="flex-1"
                    value={null}
                  />
                </div>
                {testResult && (
                  <div>
                    {testResult.success ? (
                      <Tag color="success">
                        {t("schemaAdmin.pattern.captured")}: <code>{testResult.captured}</code>
                      </Tag>
                    ) : (
                      <Tag color="default">{t("schemaAdmin.pattern.noMatch")}</Tag>
                    )}
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
